"use client";

import React, { useCallback } from "react";

import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

export const QueryLink = ({
  remove,
  set,
  children,
  className,
  "aria-label": ariaLabel,
  "data-testid": dataTestId = "query-link",
}: QueryLinkProps) => {
  const searchParams = useSearchParams();
  const path = usePathname();
  const newSearchParams = new URLSearchParams(searchParams.toString());
  if (set) {
    for (const [key, value] of Object.entries(set)) {
      newSearchParams.set(key, value);
    }
  }
  if (remove) newSearchParams.delete(remove);
  const qs = newSearchParams.toString();
  const href = qs ? `${path}?${qs}` : path;
  return (
    <Link
      href={href}
      replace={Boolean(remove)}
      scroll={false}
      className={className}
      aria-label={ariaLabel}
      data-testid={dataTestId}>
      {children}
    </Link>
  );
};

export const useRemoveQueryParam = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const newSearchParams = new URLSearchParams(searchParams.toString());
  // Get current query parameters and modify them as needed
  return useCallback(
    (param: string) => {
      newSearchParams.delete(param);
      const query = newSearchParams.toString();
      const href = query ? `${pathname}?${query}` : pathname;
      router.replace(href, { scroll: false });
    },
    [router, pathname, searchParams],
  );
};

export type QueryLinkProps = {
  /**
   * Remove this query key (after applying `set`, if any).
   * Uses history `replace` so the back button does not restore this param.
   */
  remove?: string;
  /** Merge these key/value pairs into the current query string. */
  set?: Record<string, string>;
  children?: React.ReactNode;
  className?: string;
  "aria-label"?: string;
  "data-testid"?: string;
};
